import type {NavigationError} from '@angular/router';

/** `sessionStorage` key holding the message of the chunk failure that already triggered a reload. */
export const CHUNK_RELOAD_STORAGE_KEY = 'cx:chunk-reload';

// Chrome/Edge, Firefox and Safari wording for a failed dynamic import().
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/;

/**
 * Narrows a navigation error to a failed dynamic `import()` by matching the browser's error text
 * (Chrome/Edge, Firefox, Safari). New browser wording has to be added to `CHUNK_LOAD_ERROR_PATTERN`.
 */
export function isChunkLoadError(error: unknown): error is Error {
  return error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message);
}

/**
 * Reloads the page when a lazy route chunk can no longer be loaded, which happens when the
 * running app is older than the deployed one. Reloads at most once per failing chunk and tab:
 * the failing message is remembered, so the same failure never reloads twice, while a later
 * release (new chunk names) can recover again. If storage is unavailable the guard cannot be
 * kept, so it does not reload at all rather than risk a reload loop.
 *
 * @param navigationError - the router's navigation error
 * @param getStorage - returns the tab-scoped store for the guard; defaults to `sessionStorage`
 * @param reload - performs the reload; defaults to a full page reload
 */
export function recoverFromChunkLoadError(
  {error}: NavigationError,
  getStorage: () => Storage = () => sessionStorage,
  reload: () => void = () => window.location.reload(),
): void {
  if (!isChunkLoadError(error)) {
    return;
  }
  try {
    const storage = getStorage();
    if (storage.getItem(CHUNK_RELOAD_STORAGE_KEY) === error.message) {
      return;
    }
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, error.message);
  } catch {
    return;
  }
  reload();
}
